import React from "react";
import PropTypes from "prop-types";
import R from "ramda";
import { Dialog, DialogContent, TextField } from "@mui/material";
import { LoadingButton } from "@mui/lab";
import { useFormik } from "formik";

import { useMakeMessages, useLoading, useMakes } from "hooks";
import { initialValues, validationSchema, attributesToSubmit } from "forms/MakeMessageForm";
import MakePresenter from "presenters/MakePresenter";
import SpinningCloseButton from "components/SpinningCloseButton";

import useStyles from "./useStyles";

const getTextFieldProps = () => {
  return {
    InputLabelProps: {
      shrink: true,
    },
    margin: "normal",
  };
};

const MakeMessageModal = (props) => {
  const { onClose, make } = props;
  const classes = useStyles();
  const { createMakeMessage, updateMakeMessage, deleteMakeMessage } = useMakeMessages();
  const { patchMake } = useMakes();
  const { func: createMakeMessageWithLoader, isPending: isCreatePending } = useLoading(createMakeMessage);
  const { func: updateMakeMessageWithLoader, isPending: isUpdatePending } = useLoading(updateMakeMessage);
  const { func: deleteMakeMessageWithLoader, isPending: isDeletePending } = useLoading(deleteMakeMessage);

  const makeMessage = MakePresenter.makeMessage(make);

  const formik = useFormik({
    initialValues: initialValues(makeMessage),
    validationSchema,
    validateOnBlur: false,
    onSubmit: async (values) => {
      const { sender, recipient, message } = values;
      const isEmptyFormValues = R.all(R.isEmpty)(R.map(R.trim, [sender, recipient, message]));
      const isMakeMessageExists = !R.isNil(makeMessage);

      if (isEmptyFormValues && !isMakeMessageExists) {
        onClose();
        return;
      }

      if (isEmptyFormValues && isMakeMessageExists) {
        await deleteMakeMessageWithLoader(make.id);
        onClose();
        patchMake("makeMessage", null);
      }

      if (!isEmptyFormValues && !isMakeMessageExists) {
        await createMakeMessageWithLoader(make.id, attributesToSubmit(values, make));
        onClose();
        patchMake("makeMessage", values);
      }

      if (!isEmptyFormValues && isMakeMessageExists) {
        await updateMakeMessageWithLoader(make.id, attributesToSubmit(values, make));
        onClose();
        patchMake("makeMessage", values);
      }
    },
  });
  const { touched, errors, getFieldProps, handleSubmit, isSubmitting } = formik;

  return (
    <Dialog
      classes={{ root: classes.root }}
      PaperProps={{ classes: { root: classes.paper } }}
      open
      onBackdropClick={() => onClose()}
      data-testid="makeMessageModal-dialog"
    >
      <SpinningCloseButton onClick={() => onClose()} />

      <DialogContent>
        <form onSubmit={handleSubmit}>
          <TextField
            label="To"
            placeholder="Recipient Name"
            {...getTextFieldProps()}
            {...getFieldProps("recipient")}
            helperText={touched.recipient && errors.recipient}
            error={touched.recipient && Boolean(errors.recipient)}
          />

          <TextField
            label="From"
            placeholder="Your Name"
            {...getTextFieldProps()}
            {...getFieldProps("sender")}
            helperText={touched.sender && errors.sender}
            error={touched.sender && Boolean(errors.sender)}
          />

          <TextField
            multiline
            rows="5"
            label="Message"
            placeholder="Type your message here…"
            {...getTextFieldProps()}
            {...getFieldProps("message")}
            helperText={touched.message && errors.message}
            error={touched.message && Boolean(errors.message)}
          />

          <div className={classes.actions}>
            <LoadingButton
              loading={isSubmitting}
              type="submit"
              disabled={isCreatePending || isUpdatePending || isDeletePending}
              fullWidth
              variant="outlined"
              color="primary"
            >
              Save
            </LoadingButton>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

MakeMessageModal.propTypes = {
  onClose: PropTypes.func.isRequired,
  make: MakePresenter.shape(),
};

export default MakeMessageModal;
